import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useNotification } from '../../contexts/NotificationContext';
import { useUserAPI } from '../../contexts/UserAPIContext';
import { authFetch } from "../../utils/authFetch";
import PageLoader from '../../components/PageLoader';
import { getAuth, signOut } from "firebase/auth";

const GerarUsuarioTerap = () => {

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [roles, setRoles] = useState([]);
    const [userRoleId, setUserRoleId] = useState("");
    const { showNotification } = useNotification();
    const { userAPIData } = useUserAPI();
    const [terapeuta, setTerapeuta] = useState(null);
    const [existingUser, setExistingUser] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showLogoutWarning, setShowLogoutWarning] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        const fetchTerapeuta = async () => {
            try {
                const response = await authFetch(`therapist/${id}`, { method: "GET" });
                if (!response.ok) throw new Error("Erro ao buscar terapeuta");
                const data = await response.json();
                setTerapeuta(data);

                if (data.userId != null) {
                    setIsEditMode(true);
                    const userResponse = await authFetch(`user/${data.userId}`, { method: "GET" });
                    if (!userResponse.ok) throw new Error("Erro ao buscar usuário");
                    const userData = await userResponse.json();
                    setExistingUser(userData);
                    setUsername(userData.username || "");
                    setUserRoleId(String(userData.userRoleId || ""));
                }
            } catch (err) {
                setError(err);
                showNotification("danger", "Erro ao buscar dados");
            } finally {
                setLoading(false);
            }
        };

        fetchTerapeuta();
    }, [showNotification, id]);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await authFetch('user-role', { method: "GET" });
                if (!response.ok) throw new Error("Erro ao buscar roles");
                const data = await response.json();
                setRoles(data);
            } catch (error) {
                showNotification("danger", "Erro ao carregar perfis de acesso");
            }
        };
        fetchRoles();
    }, [showNotification]);

    // Após carregar roles e terapeuta, definir o role padrão correto
    useEffect(() => {
        if (roles.length === 0 || !terapeuta || isEditMode) return;
        if (userRoleId !== "") return;

        if (String(terapeuta.therapistLevel) === '0') {
            setUserRoleId(String(roles[0].id));
        } else {
            const usuarioRole = roles.find(r =>
                r.name.toLowerCase().includes('usuário') || r.name.toLowerCase().includes('usuario')
            );
            setUserRoleId(String(usuarioRole ? usuarioRole.id : roles[0].id));
        }
    }, [roles, terapeuta, isEditMode]);

    // Roles disponíveis conforme o nível do terapeuta
    const getRolesDisponiveis = () => {
        if (!terapeuta) return roles;
        if (String(terapeuta.therapistLevel) === '0') return roles;
        const usuarioRoles = roles.filter(r =>
            r.name.toLowerCase().includes('usuário') || r.name.toLowerCase().includes('usuario')
        );
        return usuarioRoles.length > 0 ? usuarioRoles : roles;
    };

    const isEmailChanging = () => username !== (existingUser?.username || "");
    const isRoleChanging = () => String(userRoleId) !== String(existingUser?.userRoleId || "");

    const validarFormulario = () => {
        if (!isEditMode) {
            if (!username.trim()) { showNotification("danger", "Email é obrigatório"); return false; }
            if (!password) { showNotification("danger", "Senha é obrigatória"); return false; }
            if (password !== confirmPassword) { showNotification("danger", "As senhas não coincidem"); return false; }
            return true;
        }

        // Modo edição
        const emailMudou = isEmailChanging();
        const senhaMudou = !!password;
        const roleMudou = isRoleChanging();

        if (!emailMudou && !senhaMudou && !roleMudou) {
            showNotification("danger", "Nenhuma alteração detectada");
            return false;
        }
        if (senhaMudou && password !== confirmPassword) {
            showNotification("danger", "As senhas não coincidem");
            return false;
        }
        if (emailMudou) {
            if (!username.trim()) { showNotification("danger", "Email é obrigatório"); return false; }
            if (!password) { showNotification("danger", "Ao alterar o email é necessário informar a nova senha"); return false; }
        }
        return true;
    };

    const vincularTerapeuta = async (userId) => {
        const payload = { ...terapeuta, id: id, userId };
        const response = await authFetch(`therapist`, { method: 'PUT', body: JSON.stringify(payload) });
        if (!response.ok) throw new Error("Erro ao vincular terapeuta");
    };

    const handleCreateSubmit = async () => {
        const userData = {
            username,
            name: terapeuta.name,
            userRoleId: Number(userRoleId),
            password,
        };

        const response = await authFetch('user', { method: "POST", body: JSON.stringify(userData) });
        if (!response.ok) throw new Error("Erro ao criar usuário");
        const createdUser = await response.json();
        await vincularTerapeuta(createdUser.id);

        showNotification("success", "Usuário criado com sucesso!");
        navigate(`/Terapeutas/PerfilTerapeuta/${id}`);
    };

    const handleEditSubmit = async () => {
        const emailMudou = isEmailChanging();
        const senhaMudou = !!password;

        if (!emailMudou && senhaMudou) {
            // Só alterou a senha
            const response = await authFetch('user', {
                method: 'PUT',
                body: JSON.stringify({ id: existingUser.id, password }),
            });
            if (!response.ok) throw new Error("Erro ao atualizar senha");
            showNotification("success", "Senha atualizada com sucesso!");
            navigate(`/Terapeutas/PerfilTerapeuta/${id}`);
            return;
        }

        if (!emailMudou && !senhaMudou) {
            // Só alterou o perfil
            const response = await authFetch('user', {
                method: 'PUT',
                body: JSON.stringify({
                    id: existingUser.id,
                    name: existingUser.name,
                    username: existingUser.username,
                    userRoleId: Number(userRoleId),
                }),
            });
            if (!response.ok) throw new Error("Erro ao atualizar perfil");
            showNotification("success", "Perfil atualizado com sucesso!");
            navigate(`/Terapeutas/PerfilTerapeuta/${id}`);
            return;
        }

        // Email mudou → inativar antigo, desvincular, criar novo, vincular
        // 1. Inativar usuário anterior
        const inativarResponse = await authFetch('user', {
            method: 'PUT',
            body: JSON.stringify({
                id: existingUser.id,
                name: existingUser.name,
                username: existingUser.username,
                userRoleId: existingUser.userRoleId,
                enabled: false,
            }),
        });
        if (!inativarResponse.ok) throw new Error("Erro ao inativar usuário anterior");

        // 2. Desvincular terapeuta
        const desvinculaPayload = { ...terapeuta, id: id, userId: null };
        const desvinculaResponse = await authFetch('therapist', {
            method: 'PUT',
            body: JSON.stringify(desvinculaPayload),
        });
        if (!desvinculaResponse.ok) throw new Error("Erro ao desvincular terapeuta");

        // 3. Criar novo usuário
        const novoUsuario = {
            username,
            name: terapeuta.name,
            userRoleId: Number(userRoleId),
            password,
        };
        const criarResponse = await authFetch('user', { method: 'POST', body: JSON.stringify(novoUsuario) });
        if (!criarResponse.ok) throw new Error("Erro ao criar novo usuário");
        const novoUser = await criarResponse.json();

        // 4. Vincular novo usuário ao terapeuta
        await vincularTerapeuta(novoUser.id);

        // 5. Deslogar
        const auth = getAuth();
        await signOut(auth);
        navigate('/login');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validarFormulario()) return;

        // Se vai trocar email, pede confirmação antes
        if (isEditMode && isEmailChanging()) {
            setShowLogoutWarning(true);
            return;
        }

        await executarSubmit();
    };

    const executarSubmit = async () => {
        setSubmitting(true);
        try {
            if (!isEditMode) {
                await handleCreateSubmit();
            } else {
                await handleEditSubmit();
            }
        } catch (error) {
            console.error("Erro:", error);
            showNotification("danger", error.message || "Erro ao salvar usuário");
        } finally {
            setSubmitting(false);
        }
    };

    const rolesDisponiveis = getRolesDisponiveis();

    const isReadOnly = !loading && isEditMode && userAPIData && terapeuta?.userId !== userAPIData.id;

    if (loading) return <PageLoader />;
    if (error) return <p>Erro ao carregar dados.</p>;

    return (
        <div className="card">
            <div className="card-body p-3">
                {/* Modal de confirmação de logout */}
                {showLogoutWarning && (
                    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Atenção</h5>
                                </div>
                                <div className="modal-body">
                                    <p>Ao alterar o e-mail de acesso, você será <strong>desconectado automaticamente</strong> e precisará entrar novamente com o novo e-mail e senha.</p>
                                    <p>Deseja continuar?</p>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary btn-sm" onClick={() => setShowLogoutWarning(false)}>Cancelar</button>
                                    <button className="btn btn-danger btn-sm" onClick={() => { setShowLogoutWarning(false); executarSubmit(); }} disabled={submitting}>
                                        {submitting ? 'Salvando...' : 'Confirmar e sair'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="row">
                    <aside className="column">
                        <div className="side-nav">
                            <h4 className="font-weight-bolder mb-0">
                                {isEditMode ? 'Editar Usuário Terapeuta' : 'Criar Usuário Terapeuta'}
                            </h4>
                        </div>
                    </aside>
                    <div className="column-responsive column-80">
                        <div className="usuarios form content">
                            {isReadOnly && (
                                <div className="alert alert-warning p-2 mb-3 small">
                                    Você não tem permissão para editar este usuário. Somente o próprio terapeuta pode alterar seus dados de acesso.
                                </div>
                            )}
                            <form onSubmit={handleSubmit}>
                                <fieldset className="col-md-6">
                                    <label htmlFor="nome">Nome</label>
                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        className="form-control form-control-sm"
                                        value={terapeuta.name}
                                        readOnly
                                    />

                                    <label htmlFor="username">Email</label>
                                    <input
                                        type="email"
                                        name="username"
                                        id="username"
                                        className="form-control form-control-sm"
                                        value={username}
                                        onChange={(e) => !isReadOnly && setUsername(e.target.value)}
                                        readOnly={isReadOnly}
                                    />

                                    {!isReadOnly && (
                                        <>
                                            <label htmlFor="password">
                                                {isEditMode ? 'Nova Senha' : 'Senha'}
                                                {isEditMode && <small className="text-muted ms-1">(deixe em branco para não alterar)</small>}
                                            </label>
                                            <input
                                                type="password"
                                                name="password"
                                                id="password"
                                                className="form-control form-control-sm"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                            />

                                            <label htmlFor="confirmpassword">Confirmar Senha</label>
                                            <input
                                                type="password"
                                                name="confirmpassword"
                                                id="confirmpassword"
                                                className="form-control form-control-sm"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                            />
                                        </>
                                    )}

                                    <label htmlFor="userRoleId">Perfil de Acesso</label>
                                    <select
                                        name="userRoleId"
                                        id="userRoleId"
                                        className="form-control form-control-sm"
                                        value={userRoleId}
                                        onChange={(e) => setUserRoleId(e.target.value)}
                                        disabled={isReadOnly || String(terapeuta.therapistLevel) !== '0'}
                                    >
                                        {(isReadOnly ? roles : rolesDisponiveis).map((role) => (
                                            <option key={role.id} value={role.id}>
                                                {role.name}
                                            </option>
                                        ))}
                                    </select>

                                    {!isReadOnly && isEditMode && isEmailChanging() && (
                                        <div className="alert alert-warning mt-2 p-2 small">
                                            Ao alterar o e-mail você será desconectado e deverá entrar com o novo acesso.
                                        </div>
                                    )}

                                    <div className="card-footer" style={{ paddingLeft: "0" }}>
                                        <Link to={`/Terapeutas/PerfilTerapeuta/${id}`} className="btn btn-info btn-sm">Voltar</Link>
                                        {!isReadOnly && (
                                            <button type="submit" className="btn btn-primary btn-sm mx-1" disabled={submitting}>
                                                {submitting ? 'Salvando...' : 'Salvar'}
                                            </button>
                                        )}
                                    </div>
                                </fieldset>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GerarUsuarioTerap;
